import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Settings2, Plus, Edit, Trash2, RefreshCw, Building, Upload, Save, Sparkles, Key, Zap, Mail, ArrowRight, FileText, BookOpen, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate fiscal years
const generateFiscalYears = () => {
  const years = [];
  for (let i = 0; i < 10; i++) {
    const startYear = 2023 + i;
    const endYear = startYear + 1;
    years.push(`${startYear}-${endYear}`);
  }
  return years;
};

const Settings = () => {
  const { user, hasTabAccess } = useAuth();
  const canManageSettings = hasTabAccess('settings') || user?.is_super_admin;
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [initFiscalYear, setInitFiscalYear] = useState('2024-2025');
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [formData, setFormData] = useState({
    department: '',
    budget_heads: ''
  });

  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_logo: null,
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gstin: '',
    phone: '',
    email: '',
    website: '',
    approval_email_intro: '',
    approval_email_outro: ''
  });

  // ---------- Change My Password ----------
  const [pwOld, setPwOld] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwShowOld, setPwShowOld] = useState(false);
  const [pwShowNew, setPwShowNew] = useState(false);

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error('New password and confirm do not match');
      return;
    }
    if (pwNew.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await axios.put(
        `${API}/users/me/change-password`,
        { old_password: pwOld, new_password: pwNew },
        { withCredentials: true }
      );
      toast.success('Password changed successfully — please use the new password next time you log in');
      setPwOld('');
      setPwNew('');
      setPwConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const fiscalYears = generateFiscalYears();

  // ---------- AI Assistant (OpenRouter) config ----------
  const [aiConfig, setAiConfig] = useState(null);
  const [aiKeyInput, setAiKeyInput] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [aiSaving, setAiSaving] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);

  // ---------- Gmail accounts (read-only summary; full mgmt in Email Hub) ----------
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [gmailConnecting, setGmailConnecting] = useState(false);

  // ---------- PO Terms & Conditions (editable by super admin) ----------
  const [poTerms, setPoTerms] = useState('');
  const [poTermsSaving, setPoTermsSaving] = useState(false);
  const [poTermsLoaded, setPoTermsLoaded] = useState(false);

  // ---------- Books Cutoff Backfill (super-admin one-shot tool) ----------
  // Lets the accountant mark every historical invoice / payment / receipt
  // dated on/before a chosen date as already entered into the external books
  // of account — so the Accounting worklist only surfaces items that genuinely
  // still need to be posted. We always preview first, then commit.
  const cutoffToday = new Date().toISOString().slice(0, 10);
  const [cutoffDate, setCutoffDate] = useState(cutoffToday);
  const [cutoffKinds, setCutoffKinds] = useState({
    invoices: true,
    payments: true,
    receipts: true,
  });
  const [cutoffNote, setCutoffNote] = useState('');
  const [cutoffPreview, setCutoffPreview] = useState(null); // {counts, total}
  const [cutoffBusy, setCutoffBusy] = useState(false);
  const [cutoffApplying, setCutoffApplying] = useState(false);

  // ---------- Full Data Export (Settings) ----------
  // ZIP-of-CSVs dump of every entity scoped to the default tournament. The
  // download is streamed straight from /api/settings/data-export.
  const [dataExporting, setDataExporting] = useState(false);

  const handleDataExport = async () => {
    setDataExporting(true);
    try {
      const res = await axios.get(`${API}/settings/data-export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Pull filename from Content-Disposition if present, else build one.
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const fname = m ? m[1] : `mpl_data_export_${new Date().toISOString().slice(0, 10)}.zip`;
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data export downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setDataExporting(false);
    }
  };

  const _cutoffSelectedKinds = () =>
    Object.entries(cutoffKinds).filter(([, v]) => v).map(([k]) => k);

  const handleCutoffPreview = async () => {
    if (!cutoffDate) {
      toast.error('Pick a cutoff date');
      return;
    }
    const kinds = _cutoffSelectedKinds();
    if (kinds.length === 0) {
      toast.error('Select at least one record type');
      return;
    }
    setCutoffBusy(true);
    setCutoffPreview(null);
    try {
      const res = await axios.post(
        `${API}/accounting/books-cutoff`,
        { cutoff_date: cutoffDate, kinds, dry_run: true },
        { withCredentials: true }
      );
      setCutoffPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setCutoffBusy(false);
    }
  };

  const handleCutoffApply = async () => {
    if (!cutoffPreview || cutoffPreview.total === 0) {
      toast.error('Run a preview first — nothing to mark');
      return;
    }
    const ok = window.confirm(
      `Mark ${cutoffPreview.total} record(s) dated on or before ${cutoffDate} as already entered in books?\n\nThis action is logged but cannot be undone in bulk.`
    );
    if (!ok) return;
    setCutoffApplying(true);
    try {
      const res = await axios.post(
        `${API}/accounting/books-cutoff`,
        {
          cutoff_date: cutoffDate,
          kinds: _cutoffSelectedKinds(),
          note: cutoffNote || null,
          dry_run: false,
        },
        { withCredentials: true }
      );
      toast.success(`Marked ${res.data.marked} record(s) as entered in books`);
      setCutoffPreview(null);
      setCutoffNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setCutoffApplying(false);
    }
  };


  const fetchPoTerms = async () => {
    try {
      const res = await axios.get(`${API}/po-terms`, { withCredentials: true });
      setPoTerms(res.data.terms || '');
      setPoTermsLoaded(true);
    } catch (e) {
      // 401/403 are expected for non-admins; log everything else so we don't
      // silently lose useful diagnostics when something legitimately breaks.
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        console.warn('fetchPoTerms failed:', e?.response?.data || e?.message);
      }
    }
  };

  const handleSavePoTerms = async () => {
    if (!poTerms.trim()) {
      toast.error('Terms cannot be empty');
      return;
    }
    setPoTermsSaving(true);
    try {
      await axios.put(`${API}/po-terms`, { terms: poTerms }, { withCredentials: true });
      toast.success('Terms & Conditions updated for all future PO PDFs');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save terms');
    } finally {
      setPoTermsSaving(false);
    }
  };

  const fetchGmailAccounts = async () => {
    try {
      const res = await axios.get(`${API}/gmail/accounts`, { withCredentials: true });
      setGmailAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // non-admin or not configured — silent
      setGmailAccounts([]);
    }
  };

  const handleConnectGmail = async () => {
    setGmailConnecting(true);
    try {
      const res = await axios.get(`${API}/gmail/oauth/connect`, { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start OAuth flow');
      setGmailConnecting(false);
    }
  };

  const fetchAiConfig = async () => {
    try {
      const res = await axios.get(`${API}/ai/config`, { withCredentials: true });
      setAiConfig(res.data);
      setAiModel(res.data.default_model || '');
    } catch (e) {
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        console.warn('fetchAiConfig failed:', e?.response?.data || e?.message);
      }
    }
  };

  useEffect(() => {
    if (canManageSettings) fetchAiConfig();
    if (canManageSettings) fetchGmailAccounts();
    if (canManageSettings) fetchPoTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSettings]);

  const handleSaveAi = async () => {
    setAiSaving(true);
    try {
      const payload = { default_model: aiModel };
      if (aiKeyInput.trim()) payload.api_key = aiKeyInput.trim();
      await axios.post(`${API}/ai/config`, payload, { withCredentials: true });
      toast.success('AI configuration saved');
      setAiKeyInput('');
      await fetchAiConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save AI config');
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestAi = async () => {
    setAiTesting(true);
    try {
      const res = await axios.post(`${API}/ai/test-connection`, {}, { withCredentials: true });
      toast.success(`Connection OK (${res.data.model})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally {
      setAiTesting(false);
    }
  };

  const handleClearAiKey = async () => {
    if (!window.confirm('Remove the saved OpenRouter API key?')) return;
    setAiSaving(true);
    try {
      await axios.post(`${API}/ai/config`, { api_key: '' }, { withCredentials: true });
      toast.success('API key removed');
      setAiKeyInput('');
      await fetchAiConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear key');
    } finally {
      setAiSaving(false);
    }
  };

  useEffect(() => {
    if (canManageSettings) {
      fetchMetadata();
      fetchCompanySettings();
    }
  }, [user]);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/company-settings`, {
        withCredentials: true,
      });
      setCompanyData((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load company settings');
    }
  };

  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await axios.put(
        `${API}/company-settings`,
        null,
        {
          params: {
            company_name: companyData.company_name,
            address_line1: companyData.address_line1,
            address_line2: companyData.address_line2 || null,
            city: companyData.city,
            state: companyData.state,
            pincode: companyData.pincode,
            country: companyData.country,
            gstin: companyData.gstin || null,
            phone: companyData.phone || null,
            email: companyData.email || null,
            website: companyData.website || null,
            approval_email_intro: companyData.approval_email_intro || null,
            approval_email_outro: companyData.approval_email_outro || null
          },
          withCredentials: true,
        }
      );
      toast.success('Company settings saved successfully');
    } catch (error) {
      toast.error('Failed to save company settings');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/company-settings/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded successfully');
      fetchCompanySettings();
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Delete company logo?')) return;

    try {
      await axios.delete(`${API}/company-settings/logo`, {
        withCredentials: true,
      });
      toast.success('Logo deleted');
      setCompanyData(prev => ({ ...prev, company_logo: null }));
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const fetchMetadata = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/metadata/departments`, {
        withCredentials: true,
      });
      setMetadata(response.data);
    } catch (error) {
      console.error('Failed to load metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeMetadata = async () => {
    if (!window.confirm(`Initialize metadata from fiscal year ${initFiscalYear}? This will create department-budget head relationships.`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/metadata/initialize?fiscal_year=${initFiscalYear}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchMetadata();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Initialization failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const budgetHeadsArray = formData.budget_heads
      .split('\n')
      .map(h => h.trim())
      .filter(h => h);

    const payload = {
      department: formData.department,
      budget_heads: budgetHeadsArray
    };

    try {
      if (editingDept) {
        await axios.put(`${API}/metadata/departments/${editingDept.department}`, payload, {
          withCredentials: true,
        });
        toast.success('Department metadata updated successfully');
      } else {
        await axios.post(`${API}/metadata/departments`, payload, {
          withCredentials: true,
        });
        toast.success('Department metadata created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchMetadata();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      department: dept.department,
      budget_heads: dept.budget_heads.join('\n')
    });
    setDialogOpen(true);
  };

  const handleDelete = async (department) => {
    if (!window.confirm(`Delete metadata for "${department}"? This will not delete actual budgets.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/metadata/departments/${department}`, {
        withCredentials: true,
      });
      toast.success('Department metadata deleted');
      fetchMetadata();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingDept(null);
    setFormData({
      department: '',
      budget_heads: ''
    });
  };

  return (
    <div data-testid="settings-page" className="space-y-8 max-w-6xl">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account, AI assistant, and metadata
        </p>
      </div>

      <div>
        {/* User Profile */}
        <Card data-testid="profile-card" className="border-border max-w-3xl">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading">Profile Information</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Role</p>
              <Badge className="mt-1">{user?.role}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change My Password */}
      <Card data-testid="change-password-card" className="border-border max-w-3xl">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading flex items-center gap-2">
            <Key className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Update the password for your own account. Minimum 8 characters.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="pw-old">Current password</Label>
              <div className="relative">
                <Input
                  id="pw-old"
                  type={pwShowOld ? 'text' : 'password'}
                  value={pwOld}
                  onChange={(e) => setPwOld(e.target.value)}
                  autoComplete="current-password"
                  data-testid="change-pw-old-input"
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-800"
                  onClick={() => setPwShowOld((v) => !v)}
                  data-testid="change-pw-old-toggle"
                >
                  {pwShowOld ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="pw-new">New password</Label>
              <div className="relative">
                <Input
                  id="pw-new"
                  type={pwShowNew ? 'text' : 'password'}
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  autoComplete="new-password"
                  data-testid="change-pw-new-input"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-800"
                  onClick={() => setPwShowNew((v) => !v)}
                  data-testid="change-pw-new-toggle"
                >
                  {pwShowNew ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input
                id="pw-confirm"
                type={pwShowNew ? 'text' : 'password'}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="change-pw-confirm-input"
                required
                minLength={8}
              />
            </div>
            <Button
              type="submit"
              disabled={pwSaving || !pwOld || !pwNew || pwNew !== pwConfirm}
              data-testid="change-pw-submit-btn"
            >
              {pwSaving ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Company Settings - Admin Only */}
      {canManageSettings && (
        <Card data-testid="company-settings-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Company Settings
                </CardTitle>
                <CardDescription>
                  Configure company details for Purchase Order PDFs
                </CardDescription>
              </div>
              <Button onClick={handleSaveCompanySettings} disabled={savingCompany}>
                <Save className="h-4 w-4 mr-2" />
                {savingCompany ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Section */}
              <div className="md:col-span-2">
                <Label className="text-base font-semibold">Company Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  {companyData.company_logo ? (
                    <div className="relative">
                      <img
                        src={companyData.company_logo}
                        alt="Company Logo"
                        className="h-20 w-auto object-contain border rounded-lg p-2"
                      />
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={handleDeleteLogo}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="h-20 w-32 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                      No logo
                    </div>
                  )}
                  <div>
                    <input
                      type="file"
                      id="company-logo"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                    <label htmlFor="company-logo">
                      <Button variant="outline" asChild className="cursor-pointer">
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Recommended: PNG or JPG, max 500KB
                    </p>
                  </div>
                </div>
              </div>

              {/* Company Name */}
              <div className="md:col-span-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={companyData.company_name}
                  onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                  placeholder="Your Company Name"
                  className="mt-1"
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  value={companyData.address_line1}
                  onChange={(e) => setCompanyData({ ...companyData, address_line1: e.target.value })}
                  placeholder="Street address"
                  className="mt-1"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={companyData.address_line2 || ''}
                  onChange={(e) => setCompanyData({ ...companyData, address_line2: e.target.value })}
                  placeholder="Apartment, suite, etc."
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={companyData.city}
                  onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                  placeholder="City"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={companyData.state}
                  onChange={(e) => setCompanyData({ ...companyData, state: e.target.value })}
                  placeholder="State"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  value={companyData.pincode}
                  onChange={(e) => setCompanyData({ ...companyData, pincode: e.target.value })}
                  placeholder="000000"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={companyData.country}
                  onChange={(e) => setCompanyData({ ...companyData, country: e.target.value })}
                  placeholder="India"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="gstin">GSTIN</Label>
                <Input
                  id="gstin"
                  value={companyData.gstin || ''}
                  onChange={(e) => setCompanyData({ ...companyData, gstin: e.target.value.toUpperCase() })}
                  placeholder="22AAAAA0000A1Z5"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="company_phone">Phone</Label>
                <Input
                  id="company_phone"
                  value={companyData.phone || ''}
                  onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="company_email">Email</Label>
                <Input
                  id="company_email"
                  type="email"
                  value={companyData.email || ''}
                  onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                  placeholder="company@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={companyData.website || ''}
                  onChange={(e) => setCompanyData({ ...companyData, website: e.target.value })}
                  placeholder="www.company.com"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Approval email boilerplate — wraps the HTML table when the
                user clicks "Copy as HTML (email)" on an approval. */}
            <div className="mt-6 pt-6 border-t border-border/50 space-y-4">
              <div>
                <h3 className="font-semibold text-sm">Approval Email Template</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Boilerplate text wrapped around the approval table when copied as HTML for email.
                  Newlines are preserved.
                </p>
              </div>
              <div>
                <Label htmlFor="approval_email_intro">Intro (above the table)</Label>
                <Textarea
                  id="approval_email_intro"
                  rows={3}
                  value={companyData.approval_email_intro || ''}
                  onChange={(e) =>
                    setCompanyData({ ...companyData, approval_email_intro: e.target.value })
                  }
                  placeholder="Dear Sir,&#10;&#10;Please check and approve the following invoices for payment:"
                  className="mt-1"
                  data-testid="approval-intro-input"
                />
              </div>
              <div>
                <Label htmlFor="approval_email_outro">Closing (below the table)</Label>
                <Textarea
                  id="approval_email_outro"
                  rows={3}
                  value={companyData.approval_email_outro || ''}
                  onChange={(e) =>
                    setCompanyData({ ...companyData, approval_email_outro: e.target.value })
                  }
                  placeholder="Kindly revert with your approval at the earliest.&#10;&#10;Thank you,"
                  className="mt-1"
                  data-testid="approval-outro-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Department Metadata Management - Admin Only */}
      {canManageSettings && (
        <Card data-testid="metadata-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Department & Budget Head Metadata</CardTitle>
                <CardDescription>
                  Manage department-budget head relationships
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchMetadata}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" onClick={resetForm}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Department
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingDept ? 'Edit' : 'Add'} Department Metadata</DialogTitle>
                      <DialogDescription>
                        Define department and its associated budget heads
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="department">Department Name *</Label>
                        <Input
                          id="department"
                          value={formData.department}
                          onChange={(e) => setFormData({...formData, department: e.target.value})}
                          placeholder="e.g., BROADCAST, Marketing"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget_heads">Budget Heads (one per line) *</Label>
                        <textarea
                          id="budget_heads"
                          value={formData.budget_heads}
                          onChange={(e) => setFormData({...formData, budget_heads: e.target.value})}
                          placeholder="Enter budget heads, one per line"
                          className="w-full min-h-[200px] p-3 border rounded-md text-sm"
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Enter each budget head on a new line
                        </p>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">
                          {editingDept ? 'Update' : 'Create'} Metadata
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Initialize from Budget Data */}
            {metadata.length === 0 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Settings2 className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-2">Initialize Metadata</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Create department-budget head relationships from existing budget data
                    </p>
                    <div className="flex items-center gap-3">
                      <Select value={initFiscalYear} onValueChange={setInitFiscalYear}>
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fiscalYears.map((year) => (
                            <SelectItem key={year} value={year}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleInitializeMetadata} size="sm">
                        Initialize from {initFiscalYear}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Metadata List */}
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : metadata.length === 0 ? (
              <div className="text-center py-8">
                <Settings2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-muted-foreground">No metadata configured</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Initialize from budget data or add manually
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {metadata.map((dept) => (
                  <div
                    key={dept.id}
                    data-testid={`metadata-${dept.department}`}
                    className="border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg">{dept.department}</h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {dept.budget_heads.slice(0, 10).map((head) => (
                            <Badge key={head} variant="outline" className="text-xs">
                              {head}
                            </Badge>
                          ))}
                          {dept.budget_heads.length > 10 && (
                            <Badge variant="outline" className="text-xs">
                              +{dept.budget_heads.length - 10} more
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Total: {dept.budget_heads.length} budget heads
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(dept)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(dept.department)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Gmail Accounts Quick Access (Admin Only) */}
      {canManageSettings && (
        <Card data-testid="gmail-settings-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Connected Gmail Accounts
                </CardTitle>
                <CardDescription>
                  Shared mailboxes synced into the Email Hub. Refresh tokens are encrypted at rest.
                </CardDescription>
              </div>
              <Badge variant={gmailAccounts.length > 0 ? 'default' : 'outline'} data-testid="gmail-status-badge">
                {gmailAccounts.length} connected
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {gmailAccounts.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No Gmail accounts connected yet. Connect a shared mailbox to start AI-powered task extraction.
              </div>
            ) : (
              <div className="space-y-2">
                {gmailAccounts.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2"
                    data-testid={`gmail-acct-${a.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{a.label || a.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{a.email}</div>
                    </div>
                    {a.auth_error ? (
                      <Badge variant="destructive">Reconnect</Badge>
                    ) : (
                      <Badge variant="outline">Active</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                onClick={handleConnectGmail}
                disabled={gmailConnecting}
                data-testid="settings-connect-gmail-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                {gmailConnecting ? 'Redirecting…' : 'Connect Gmail Account'}
              </Button>
              <Link to="/email-hub">
                <Button variant="outline" data-testid="settings-open-email-hub-button">
                  Open Email Hub
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PO Document Terms & Conditions (Super Admin Only) */}
      {canManageSettings && poTermsLoaded && (
        <Card data-testid="po-terms-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Purchase Order — Terms &amp; Conditions
            </CardTitle>
            <CardDescription>
              Edit the T&amp;C block printed on every PO PDF. Use one clause per line; numbering is preserved as you type.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-3">
            <Textarea
              value={poTerms}
              onChange={(e) => setPoTerms(e.target.value)}
              rows={10}
              className="font-mono text-xs"
              data-testid="po-terms-textarea"
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-muted-foreground">
                Tip: keep each clause short and quote PO number / GST / TDS rules so vendors don't push back later.
              </p>
              <Button
                onClick={handleSavePoTerms}
                disabled={poTermsSaving}
                data-testid="po-terms-save-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {poTermsSaving ? 'Saving…' : 'Save Terms'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Data Export (Admin Only) */}
      {(user?.is_super_admin || user?.role === 'admin') && (
        <Card data-testid="data-export-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Download className="h-5 w-5 text-accent" />
                  Full Data Export
                </CardTitle>
                <CardDescription>
                  Download a ZIP of CSVs covering every entity for the default
                  tournament — Budgets, Purchase Orders, Invoices, Payments,
                  Revenue (Sales Orders / Receipts), Approvals, Vendors, Bank
                  Transactions and Accounting status.
                </CardDescription>
              </div>
              <Badge variant="outline">Admin</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="rounded-md bg-muted/40 border p-3 text-xs text-muted-foreground leading-relaxed">
              The export is scoped to the tournament currently marked as
              <strong className="mx-1">default</strong>
              in the Tournaments table. Vendors, financial accounts and bank
              transactions are master data and are included in full.
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                onClick={handleDataExport}
                disabled={dataExporting}
                data-testid="data-export-download-button"
              >
                <Download className={`h-4 w-4 mr-2 ${dataExporting ? 'animate-pulse' : ''}`} />
                {dataExporting ? 'Preparing ZIP…' : 'Download Data Export (ZIP)'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Books Cutoff Backfill (Super-Admin Only) */}
      {user?.is_super_admin && (
        <Card data-testid="books-cutoff-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-accent" />
                  Books Cutoff Backfill
                </CardTitle>
                <CardDescription>
                  Bulk-mark every pending Invoice, Payment and Receipt dated on
                  or before the chosen date as already entered in the external
                  books of account. Use this once after enabling the Accounting
                  worklist so historical data doesn't clutter the queue.
                </CardDescription>
              </div>
              <Badge variant="outline">Super Admin</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cutoff-date">Cutoff date (inclusive)</Label>
                <Input
                  id="cutoff-date"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => { setCutoffDate(e.target.value); setCutoffPreview(null); }}
                  data-testid="books-cutoff-date-input"
                />
                <p className="text-xs text-muted-foreground">
                  Records dated up to end-of-day {cutoffDate || '—'} will be
                  marked as entered.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Record types</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'invoices', label: 'Invoices' },
                    { key: 'payments', label: 'Payments' },
                    { key: 'receipts', label: 'Receipts' },
                  ].map(({ key, label }) => {
                    const on = cutoffKinds[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setCutoffKinds((p) => ({ ...p, [key]: !p[key] }));
                          setCutoffPreview(null);
                        }}
                        data-testid={`books-cutoff-toggle-${key}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          on
                            ? 'bg-accent text-accent-foreground border-accent'
                            : 'bg-background text-muted-foreground border-border hover:border-foreground/40'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cutoff-note">Audit note (optional)</Label>
              <Input
                id="cutoff-note"
                placeholder="e.g. Migrated from Tally on 12-Feb-2026"
                value={cutoffNote}
                onChange={(e) => setCutoffNote(e.target.value)}
                data-testid="books-cutoff-note-input"
              />
            </div>

            {cutoffPreview && (
              <div
                data-testid="books-cutoff-preview"
                className="rounded-md border border-border bg-muted/30 p-4 space-y-2"
              >
                <div className="text-sm font-semibold">
                  Preview · {cutoffPreview.total} record(s) will be marked
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  {['invoices', 'payments', 'receipts'].map((k) => (
                    <div key={k} className="flex items-center justify-between bg-background border border-border/60 rounded px-3 py-2">
                      <span className="capitalize text-muted-foreground">{k}</span>
                      <span className="font-semibold tabular-nums">
                        {cutoffPreview.counts?.[k] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                variant="outline"
                onClick={handleCutoffPreview}
                disabled={cutoffBusy || cutoffApplying}
                data-testid="books-cutoff-preview-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${cutoffBusy ? 'animate-spin' : ''}`} />
                {cutoffBusy ? 'Counting…' : 'Preview'}
              </Button>
              <Button
                onClick={handleCutoffApply}
                disabled={
                  cutoffApplying ||
                  cutoffBusy ||
                  !cutoffPreview ||
                  cutoffPreview.total === 0
                }
                data-testid="books-cutoff-apply-button"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                {cutoffApplying
                  ? 'Marking…'
                  : cutoffPreview
                  ? `Mark ${cutoffPreview.total} as entered`
                  : 'Mark as entered'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Assistant Configuration (Admin Only) */}
      {canManageSettings && aiConfig && (
        <Card data-testid="ai-settings-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-accent" />
                  AI Assistant (OpenRouter)
                </CardTitle>
                <CardDescription>
                  Configure the OpenRouter API key used by the in-app AI Assistant.
                </CardDescription>
              </div>
              <Badge
                variant={aiConfig.configured ? 'default' : 'outline'}
                data-testid="ai-config-status-badge"
              >
                {aiConfig.configured ? 'Configured' : 'Not configured'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="rounded-md bg-muted/40 border p-3 text-xs text-muted-foreground leading-relaxed">
              Get your API key from{' '}
              <a
                href="https://openrouter.ai/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                openrouter.ai/keys
              </a>
              . The key is encrypted at rest and never sent back to the browser. Usage is billed
              directly on your OpenRouter account — no token caps are enforced here.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ai-api-key" className="flex items-center gap-1.5">
                  <Key className="h-3.5 w-3.5" />
                  OpenRouter API Key
                </Label>
                <Input
                  id="ai-api-key"
                  type="password"
                  value={aiKeyInput}
                  onChange={(e) => setAiKeyInput(e.target.value)}
                  placeholder={aiConfig.configured ? '••••••••••  (leave blank to keep existing)' : 'sk-or-v1-...'}
                  data-testid="ai-api-key-input"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-default-model">Default Model</Label>
                <Select value={aiModel} onValueChange={setAiModel}>
                  <SelectTrigger id="ai-default-model" data-testid="ai-default-model-select">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {(aiConfig.supported_models || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Users can override per-chat from the AI Assistant page.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                onClick={handleSaveAi}
                disabled={aiSaving}
                data-testid="ai-save-config-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {aiSaving ? 'Saving…' : 'Save Configuration'}
              </Button>
              <Button
                variant="outline"
                onClick={handleTestAi}
                disabled={!aiConfig.configured || aiTesting}
                data-testid="ai-test-connection-button"
              >
                <Zap className="h-4 w-4 mr-2" />
                {aiTesting ? 'Testing…' : 'Test Connection'}
              </Button>
              {aiConfig.configured && (
                <Button
                  variant="ghost"
                  onClick={handleClearAiKey}
                  disabled={aiSaving}
                  data-testid="ai-clear-key-button"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Key
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings;
